import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import qs from "qs";
import Swal from "sweetalert2";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import InputNumeric from "../../../components/InputNumeric"; // adjust path if needed

export default function CompanyCreate({
    open,
    onClose,
    onSuccess,
}) {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [listCompanyGroup, setListCompanyGroup] = useState([]);

    const [inputCompany, setInputCompany] = useState("");
    const [selectedCompanyGroup, setSelectedCompanyGroup] = useState(0);
    const [selectedBoundary, setSelectedBoundary] = useState(0); // shareholding (%)
    const [inputBuilding, setInputBuilding] = useState("");
    const [inputAddress, setInputAddress] = useState("");

    const apiPost = useCallback(async (path, data) => {
        const options = {
            withCredentials: true,
            method: "POST",
            headers: { "content-type": "application/x-www-form-urlencoded" },
            data: qs.stringify(data || {}),
            url: `${process.env.REACT_APP_BASE_SERVER}${path}`,
        };
        const res = await axios(options);
        return res?.data;
    }, []);

    // ✅ fetch divisions for dropdown (same as old CompanyCreate)
    useEffect(() => {
        if (!open) return;

        let alive = true;

        (async () => {
            try {
                setLoading(true);
                const data = {};
                const res = await apiPost("/client/division", data);
                if (!alive) return;

                if (res?.success) {
                    setListCompanyGroup(Array.isArray(res.data) ? res.data : []);
                } else {
                    setListCompanyGroup([]);
                }
            } catch (e) {
                console.error(e);
                if (alive) setListCompanyGroup([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [apiPost, open]);

    const reset = () => {
        setInputCompany("");
        setSelectedCompanyGroup(0);
        setSelectedBoundary(0);
        setInputBuilding("");
        setInputAddress("");
    };

    const handleClose = () => {
        if (loading) return;
        reset();
        onClose?.();
    };

    const onInputShareholding = (values, sourceInfo) => {
        if (sourceInfo?.event?.target?.name) {
            setSelectedBoundary(values?.value ? Number(values.value) : 0);
        }
    };

    const handleSave = async () => {
        const companyName = (inputCompany || "").trim();

        // validation
        if (!companyName || Number(selectedCompanyGroup) === 0 || Number(selectedBoundary) === 0) {
            // ✅ close first
            reset();
            onClose?.();

            Swal.fire({
                title: "",
                html: "<p>Please fill all information needs</p>",
                showCancelButton: false,
                confirmButtonColor: "#3085d6",
                confirmButtonText: "Ok",
            });
            return;
        }

        try {
            setLoading(true);

            const payload = {
                name: companyName,
                group: Number(selectedCompanyGroup),
                boundary: Number(selectedBoundary),
                building: inputBuilding,
                address: inputAddress,
            };

            const res = await apiPost("/client/company/create", payload);

            // ✅ close first (success or error)
            reset();
            onSuccess?.(); // still safe even if error? better only on success; see below
            onClose?.();

            if (!res?.success) {
                Swal.fire({
                    title: "",
                    html: "<p>Cannot save data, please try again later</p>",
                    showCancelButton: false,
                    confirmButtonColor: "#3085d6",
                    confirmButtonText: "Ok",
                });
                return;
            }

            Swal.fire({
                title: "",
                html: "<p>Successfully record company</p>",
                showCancelButton: false,
                confirmButtonColor: "#3085d6",
                confirmButtonText: "Ok",
            });
        } catch (e) {
            console.error(e);

            // ✅ close first on exception too
            reset();
            onClose?.();

            Swal.fire({
                title: "",
                html: "<p>Cannot save data, please try again later</p>",
                showCancelButton: false,
                confirmButtonColor: "#3085d6",
                confirmButtonText: "Ok",
            });
        } finally {
            setLoading(false);
        }
    };


    return (
        <Dialog open={!!open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                    {t("general.btnCreate")} {t("menu.label622")}
                </Typography>
                <IconButton onClick={handleClose} disabled={loading}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3" id="tour__company_name">
                            <label htmlFor="company_name" className="form-label">
                                {t("menu.label622")}
                            </label>
                            <input
                                id="company_name"
                                name="company_name"
                                type="text"
                                className="form-control"
                                value={inputCompany}
                                onChange={(e) => setInputCompany(e.target.value)}
                                placeholder={t("emission.form.enter")}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3" id="tour__company_division">
                            <label htmlFor="company_division" className="form-label">
                                {t("menu.label621")}
                            </label>
                            <select
                                id="company_division"
                                name="company_division"
                                className="form-select mb-3"
                                onChange={(e) => setSelectedCompanyGroup(e.target.value)}
                                value={selectedCompanyGroup}
                                disabled={loading}
                            >
                                <option value={0}>{t("company.form.label1")}</option>
                                {listCompanyGroup.map((item, idx) => (
                                    <option key={idx + 1} value={item.group_id}>
                                        {item.group_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3" id="tour__company_shareholding">
                            <label htmlFor="shareholding" className="form-label">
                                {t("company.label1")}
                            </label>
                            <div className="input-group">
                                <InputNumeric
                                    id="shareholding"
                                    name="shareholding"
                                    isAllowed={(values) => {
                                        if (!values.value) return true;
                                        const { floatValue } = values;
                                        return floatValue < 101;
                                    }}
                                    value={selectedBoundary}
                                    onValueChange={onInputShareholding}
                                    placeholder={t("emission.form.enter")}
                                    disabled={loading}
                                />
                                <span className="input-group-text" id="basic-addon2">
                                    %
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3" id="tour__company_location">
                            <label htmlFor="building" className="form-label">
                                {t("menu.label623")}
                            </label>
                            <input
                                id="building"
                                name="building"
                                type="text"
                                className="form-control"
                                value={inputBuilding}
                                onChange={(e) => setInputBuilding(e.target.value)}
                                placeholder={t("emission.form.enter")}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="col-md-12">
                        <div className="mb-3" id="tour__company_address">
                            <label htmlFor="address" className="form-label">
                                {t("location.label1")}
                            </label>
                            <input
                                id="address"
                                name="address"
                                type="text"
                                className="form-control"
                                value={inputAddress}
                                onChange={(e) => setInputAddress(e.target.value)}
                                placeholder={t("emission.form.enter")}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="outlined" onClick={handleClose} disabled={loading}>
                    {t("general.btnCancel") || "Cancel"}
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={loading}>
                    {t("general.btnSave")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
